import {
  Banner,
  Box,
  Button,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import {
  CollectionDocument,
  CollectionsFetchDocument,
  CollectionTreeDocument,
  useCollectionDetachMutation,
} from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';

function useCollectionDetachMutationImpl(): ReturnType<
  typeof useCollectionDetachMutation
> {
  const [detachFn, opts] = useCollectionDetachMutation({
    refetchQueries: (response) => [
      {
        query: CollectionDocument,
        variables: {
          id: response.data?.collectionUnlock?.id || '',
        },
      },
      { query: CollectionsFetchDocument },
      {
        query: CollectionTreeDocument,
        variables: {
          id: response.data?.collectionUnlock?.id || '',
        },
      },
    ],
  });
  return [detachFn, opts];
}

type CollectionDetachModalContentProps = {
  collectionId: string;
};
export function CollectionDetachModal({
  collectionId,
}: CollectionDetachModalContentProps): JSX.Element {
  const { hideModal } = useModal();
  const [detachFn, { loading }] = useCollectionDetachMutationImpl();
  const { successNotify, errorNotify } = useNotification();

  async function onDetach() {
    try {
      await detachFn({ variables: { id: collectionId } });
      hideModal();
      successNotify({
        title: 'Success',
        text: 'The collection has been detached from its Elevio source.',
      });
    } catch (_error) {
      errorNotify({
        text: 'Something went wrong. Please try again.',
      });
    }
  }

  return (
    <ModalWrapper>
      <ModalHead title="Detach collection" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Banner
          variant="fail"
          description={
            <Box>
              <Text.Paragraph variant="2" subdued color="red.800">
                Are you sure you want to detach this imported collection?
              </Text.Paragraph>
              <ul>
                <li>
                  <Text.Paragraph variant="2" subdued color="red.800">
                    Future changes to the original Elevio workspace will no
                    longer be tracked.
                  </Text.Paragraph>
                </li>
                <li>
                  <Text.Paragraph variant="2" subdued color="red.800">
                    A detached collection will enable full
                    editing/moving/deletion of articles and categories.
                  </Text.Paragraph>
                </li>
              </ul>
            </Box>
          }
        />
      </ModalBody>
      <ModalFoot>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button disabled={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button variant="danger" disabled={loading} onClick={onDetach}>
            {loading ? 'Detaching...' : 'Detach'}
          </Button>
        </FlexBox>
      </ModalFoot>
    </ModalWrapper>
  );
}
